import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { seedCategories, checkCategoriesExist } from '../lib/seedCategories';
import { getAllCategories } from '../lib/categories';
import { fixOrdersMissingSellerId, fixOrdersMissingUserId, fixOrderById } from '../utils/orderApi';

// Style guide colors (matching AllProducts.js)
const NEUTRAL_BG = '#F8F9FA';
const NEUTRAL_WHITE = '#FFFFFF';
const NEUTRAL_TEXT = '#1A1A1A';
const ACCENT_GRAY = '#6B7280';
const PRIMARY_BROWN = '#8B4513';
const ERROR_RED = '#DC2626';
const SUCCESS_GREEN = '#059669';

// Font styles
const headingFont = { fontFamily: 'Inter, system-ui, sans-serif' };
const bodyFont = { fontFamily: 'Inter, system-ui, sans-serif' };

export function AdminTools() {
  const navigate = useNavigate();
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedingStatus, setSeedingStatus] = useState('');
  const [categoriesExist, setCategoriesExist] = useState(false);
  const [isFixingOrders, setIsFixingOrders] = useState(false);
  const [orderFixStatus, setOrderFixStatus] = useState('');

  const handleSeedCategories = async () => {
    setIsSeeding(true);
    setSeedingStatus('Checking existing categories...');
    
    try {
      const exist = await checkCategoriesExist();
      setCategoriesExist(exist);
      
      if (exist) {
        setSeedingStatus('Categories already exist in database. No action needed.');
        return;
      }
      
      setSeedingStatus('Seeding categories...');
      await seedCategories();
      setSeedingStatus('✅ Categories seeded successfully!');
      setCategoriesExist(true);
    } catch (error) {
      setSeedingStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsSeeding(false);
    }
  };

  const handleFixOrders = async () => {
    setIsFixingOrders(true);
    setOrderFixStatus('Fixing orders missing sellerId...');
    
    try {
      const result = await fixOrdersMissingSellerId();
      setOrderFixStatus(`✅ Fixed ${result.fixed} orders successfully!`);
    } catch (error) {
      setOrderFixStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsFixingOrders(false);
    }
  };

  const handleFixSpecificOrder = async () => {
    setIsFixingOrders(true);
    setOrderFixStatus('Fixing specific order dsRmsDcfgTPwfirOsRL6...');
    
    try {
      // Fix the specific order that's causing the chat issue
      const result = await fixOrderById('dsRmsDcfgTPwfirOsRL6', 'sf76MdxXTdeRWhVKvvFCmATJRu33');
      setOrderFixStatus(`✅ Fixed order ${result.orderId} successfully!`);
    } catch (error) {
      setOrderFixStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsFixingOrders(false);
    }
  };

  const handleFixOrdersMissingUserId = async () => {
    setIsFixingOrders(true);
    setOrderFixStatus('Fixing orders missing userId...');
    
    try {
      const result = await fixOrdersMissingUserId();
      setOrderFixStatus(`✅ Fixed ${result.fixed} orders missing userId!`);
    } catch (error) {
      setOrderFixStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsFixingOrders(false);
    }
  };

  const categories = getAllCategories();

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: NEUTRAL_BG, 
      padding: '20px' 
    }}>
      {/* Header */}
      <header style={{ 
        background: NEUTRAL_WHITE,
        borderRadius: 12,
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <div>
            <h1 style={{
              fontSize: 24,
              fontWeight: 700,
              color: NEUTRAL_TEXT,
              margin: '0 0 8px 0',
              ...headingFont
            }}>
              Admin Tools
            </h1>
            <p style={{
              fontSize: 14,
              color: ACCENT_GRAY,
              margin: 0,
              ...bodyFont
            }}>
              Database management and system utilities
            </p>
          </div>
          <button
            onClick={() => navigate('/all-products')}
            style={{
              background: NEUTRAL_BG,
              color: NEUTRAL_TEXT,
              border: '1px solid #E0E0E0',
              borderRadius: 8,
              padding: '12px 20px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              ...bodyFont
            }}
          >
            Back to Products
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '24px',
        maxWidth: 1200,
        margin: '0 auto'
      }}>
        
        {/* Category Management */}
        <div style={{
          background: NEUTRAL_WHITE,
          borderRadius: 12,
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{
            fontSize: 20,
            fontWeight: 600,
            color: NEUTRAL_TEXT,
            margin: '0 0 16px 0',
            ...headingFont
          }}>
            Category Management
          </h2>
          
          <div style={{ marginBottom: '20px' }}>
            <p style={{
              fontSize: 14,
              color: ACCENT_GRAY,
              margin: '0 0 16px 0',
              ...bodyFont
            }}>
              Seed the database with the predefined category structure.
            </p>
            
            <button
              onClick={handleSeedCategories}
              disabled={isSeeding}
              style={{
                background: isSeeding ? ACCENT_GRAY : PRIMARY_BROWN,
                color: NEUTRAL_WHITE,
                border: 'none',
                borderRadius: 8,
                padding: '12px 24px',
                fontSize: 14,
                fontWeight: 600,
                cursor: isSeeding ? 'not-allowed' : 'pointer',
                opacity: isSeeding ? 0.6 : 1,
                ...bodyFont
              }}
            >
              {isSeeding ? 'Seeding...' : 'Seed Categories'}
            </button>
            
            {seedingStatus && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                borderRadius: 8,
                background: seedingStatus.includes('✅') ? '#F0FDF4' : 
                           seedingStatus.includes('❌') ? '#FEF2F2' : '#F0F9FF',
                border: `1px solid ${seedingStatus.includes('✅') ? SUCCESS_GREEN : 
                                   seedingStatus.includes('❌') ? ERROR_RED : '#0EA5E9'}`,
                color: seedingStatus.includes('✅') ? SUCCESS_GREEN : 
                       seedingStatus.includes('❌') ? ERROR_RED : '#0EA5E9',
                fontSize: 14,
                ...bodyFont
              }}>
                {seedingStatus}
              </div>
            )}
          </div>
          
          {/* Order Fixing Section */}
          <div style={{ marginBottom: '20px' }}>
            <p style={{
              fontSize: 14,
              color: ACCENT_GRAY,
              margin: '0 0 16px 0',
              ...bodyFont
            }}>
              Fix existing orders that are missing the sellerId field (for chat functionality).
            </p>
            
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={handleFixOrders}
                disabled={isFixingOrders}
                style={{
                  background: isFixingOrders ? ACCENT_GRAY : SUCCESS_GREEN,
                  color: NEUTRAL_WHITE,
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 24px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: isFixingOrders ? 'not-allowed' : 'pointer',
                  opacity: isFixingOrders ? 0.6 : 1,
                  ...bodyFont
                }}
              >
                {isFixingOrders ? 'Fixing...' : 'Fix All Orders Missing SellerId'}
              </button>
              
              <button
                onClick={handleFixSpecificOrder}
                disabled={isFixingOrders}
                style={{
                  background: isFixingOrders ? ACCENT_GRAY : PRIMARY_BROWN,
                  color: NEUTRAL_WHITE,
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 24px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: isFixingOrders ? 'not-allowed' : 'pointer',
                  opacity: isFixingOrders ? 0.6 : 1,
                  ...bodyFont
                }}
              >
                {isFixingOrders ? 'Fixing...' : 'Fix Specific Order (Chat Issue)'}
              </button>
              
              <button
                onClick={handleFixOrdersMissingUserId}
                disabled={isFixingOrders}
                style={{
                  background: isFixingOrders ? ACCENT_GRAY : '#10B981',
                  color: NEUTRAL_WHITE,
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 24px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: isFixingOrders ? 'not-allowed' : 'pointer',
                  opacity: isFixingOrders ? 0.6 : 1,
                  ...bodyFont
                }}
              >
                {isFixingOrders ? 'Fixing...' : 'Fix Orders Missing UserId'}
              </button>
            </div>
            
            {orderFixStatus && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                borderRadius: 8,
                background: orderFixStatus.includes('✅') ? '#F0FDF4' : 
                           orderFixStatus.includes('❌') ? '#FEF2F2' : '#F0F9FF',
                border: `1px solid ${orderFixStatus.includes('✅') ? SUCCESS_GREEN : 
                                   orderFixStatus.includes('❌') ? ERROR_RED : '#0EA5E9'}`,
                color: orderFixStatus.includes('✅') ? SUCCESS_GREEN : 
                       orderFixStatus.includes('❌') ? ERROR_RED : '#0EA5E9',
                fontSize: 14,
                ...bodyFont
              }}>
                {orderFixStatus}
              </div>
            )}
          </div>
          
          <div style={{
            background: NEUTRAL_BG,
            borderRadius: 8,
            padding: '16px'
          }}>
            <h3 style={{
              fontSize: 16,
              fontWeight: 600,
              color: NEUTRAL_TEXT,
              margin: '0 0 12px 0',
              ...bodyFont
            }}>
              Available Categories ({categories.length})
            </h3>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              {categories.map(category => (
                <div key={category.categoryId} style={{
                  fontSize: 14,
                  color: NEUTRAL_TEXT,
                  ...bodyFont
                }}>
                  <strong>{category.categoryName}</strong>
                  <span style={{ color: ACCENT_GRAY, marginLeft: '8px' }}>
                    ({category.subCategories.length} sub-categories)
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* System Information */}
        <div style={{
          background: NEUTRAL_WHITE,
          borderRadius: 12,
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{
            fontSize: 20,
            fontWeight: 600,
            color: NEUTRAL_TEXT,
            margin: '0 0 16px 0',
            ...headingFont
          }}>
            System Information
          </h2>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div>
              <h3 style={{
                fontSize: 16,
                fontWeight: 600,
                color: NEUTRAL_TEXT,
                margin: '0 0 8px 0',
                ...bodyFont
              }}>
                Database Status
              </h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: categoriesExist ? SUCCESS_GREEN : ACCENT_GRAY
                }} />
                <span style={{
                  fontSize: 14,
                  color: NEUTRAL_TEXT,
                  ...bodyFont
                }}>
                  Categories: {categoriesExist ? 'Seeded' : 'Not seeded'}
                </span>
              </div>
            </div>
            
            <div>
              <h3 style={{
                fontSize: 16,
                fontWeight: 600,
                color: NEUTRAL_TEXT,
                margin: '0 0 8px 0',
                ...bodyFont
              }}>
                Category Structure
              </h3>
              <div style={{
                fontSize: 14,
                color: ACCENT_GRAY,
                ...bodyFont
              }}>
                <div>• {categories.length} main categories</div>
                <div>• {categories.reduce((total, cat) => total + cat.subCategories.length, 0)} total sub-categories</div>
                <div>• Hierarchical organization</div>
                <div>• Firebase Firestore storage</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminTools; 